import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import PtfButton from "@/components/PtfButton";
import { Reveal, MaskText } from "@/components/motion/Reveal";
import { EvidenceChip } from "@/components/Evidence";

const Eyebrow = ({ children, light }) => (
  <p className={`text-[0.7rem] uppercase tracking-[0.28em] mb-5 flex items-center gap-3 ${light ? "text-ptf-accent" : "text-ptf-primary/60"}`}>
    <span className={`h-px w-8 inline-block ${light ? "bg-ptf-accent" : "bg-ptf-primary/40"}`} />
    {children}
  </p>
);
const SECTIONS = [
  ["The story", "A remote community in Uttarakhand, and the belief that adventure, tourism and hard field work can change what a valley expects of its future."],
  ["The community", "The people of the adopted villages: farmers, mothers, school children, filmed with respect, on their own terms."],
  ["The journey", "The trek in, the rivers, the seasons: what it takes simply to reach the place where the work happens."],
  ["The field", "Solar panels, hand pumps, classrooms and homestays: the interventions, as they were carried out."],
  ["The outcome", "What changed, what remains, and what the community itself makes of it. Documented outcomes only."],
];

export default function DawnFilm() {
  return (
    <>
      <Seo title="Dawn: A Project of Belief" path="/dawn/film" description="Dawn: A Project of Belief is a documentary film in development around Project Dawn and a remote Uttarakhand community. Watch the teasers." />
      <PageHero
        eyebrow="Film · In development"
        titleLines={["Dawn.", "A project", "of belief."]}
        intro="An approximately 45 minute documentary built around Project Dawn and a remote community in Uttarakhand, now seeking collaboration, sponsorship and partners to reach film festivals in India and internationally, together with Laxmi's story."
        image="https://customer-assets-gfyr7b9c.emergentagent.net/job_planet-purpose/artifacts/0la6er80_05.webp"
      />

      <section className="bg-ptf-primary text-ptf-bg py-16 md:py-24">
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12">
          <Reveal><Eyebrow light>Watch the teasers</Eyebrow></Reveal>
          <MaskText
            lines={["The film,", "as it begins."]}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight"
          />
          <Reveal delay={0.15}>
            <p className="mt-5 text-ptf-bg/70 leading-relaxed max-w-2xl text-sm">
              Two teasers from the Dawn footage, published by the team. The full
              documentary is in production and will premiere here.
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { id: "ki7jw5weHfw", title: "Dawn · teaser I" },
              { id: "AnsRzO7z42g", title: "Dawn · teaser II" },
            ].map((v) => (
              <Reveal key={v.id}>
                <figure>
                  <div className="aspect-video border border-ptf-bg/15 bg-ptf-bg/5">
                    <iframe
                      data-testid={`teaser-embed-${v.id}`}
                      src={`https://www.youtube.com/embed/${v.id}`}
                      title={v.title}
                      className="h-full w-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                  <figcaption className="mt-2 flex items-center justify-between gap-3 text-[0.65rem] uppercase tracking-[0.12em] text-ptf-bg/55">
                    {v.title}
                    <EvidenceChip status="DOCUMENTED" />
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-ptf-primary/10 py-16 md:py-24">
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12">
          <Reveal><Eyebrow>From the field · Watch</Eyebrow></Reveal>
          <MaskText
            lines={["The footage", "already exists."]}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary"
          />
          <Reveal delay={0.15}>
            <p className="mt-5 text-ptf-text/70 leading-relaxed max-w-2xl">
              A documented Project Dawn film from the field, published by the team,
              the raw material the feature documentary grows from.
            </p>
          </Reveal>
          <div className="mt-10 max-w-3xl">
            <figure>
              <div className="aspect-video border border-ptf-primary/12 bg-ptf-primary/5">
                <iframe
                  data-testid="film-embed-7zVsjuEfl40"
                  src="https://www.youtube.com/embed/7zVsjuEfl40"
                  title="Project Dawn · field film"
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <figcaption className="mt-2 text-[0.65rem] uppercase tracking-[0.12em] text-ptf-text/50">Project Dawn · field film</figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section className="bg-ptf-bg py-16 md:py-24">
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12">
          <Reveal><Eyebrow>What the film will hold</Eyebrow></Reveal>
          <MaskText
            lines={["Belief, on record."]}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary"
          />
          <div className="mt-12 space-y-0">
            {SECTIONS.map(([t, x], i) => (
              <Reveal key={t} delay={i * 0.05}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 py-7 border-t border-ptf-primary/12">
                  <div className="md:col-span-1 font-display text-3xl text-ptf-primary/25">{String(i + 1).padStart(2, "0")}</div>
                  <div className="md:col-span-4 font-display uppercase text-2xl tracking-tight text-ptf-primary">{t}</div>
                  <div className="md:col-span-7 text-ptf-text/70 leading-relaxed">{x}</div>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 text-xs text-ptf-text/50 leading-relaxed max-w-2xl">
            Dawn: A Project of Belief is seeking collaboration, sponsorship and
            partners to feature in film festivals, alongside{" "}
            <a href="/dawn/laxmi" className="underline underline-offset-2">Laxmi's story</a>.
            No selections or awards are claimed; screenings and reception will be
            documented here as they happen.
          </p>
        </div>
      </section>

      <section className="bg-ptf-secondary text-ptf-bg py-20 md:py-28">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <h2 className="font-display font-extrabold uppercase text-4xl md:text-5xl tracking-tight leading-[0.95]">Films need partners<br />as much as cameras.</h2>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <PtfButton to="/partnerships" variant="accent" data-testid="film-partner-cta">Partner on the Film</PtfButton>
            <PtfButton to="/flagship" variant="outlineLight">Documentary Expeditions</PtfButton>
          </div>
        </div>
      </section>
    </>
  );
}
